/**
 * fbAccountToCompany — read-only mirror adapter (GAM: Fireberry source accounts)
 *
 * Maps a GAM `fb_records` row where object_type_id = 1 (Fireberry "לקוח" /
 * Account = בית עסק) into a flat, read-only shape for the `fireberry_accounts`
 * resource. Separate from talents / contacts / fireberry_contacts.
 *
 * The mirror is read-only by contract: nothing is written back, no data is
 * copied, no `fb_records` structure is touched, no RLS is changed.
 */

/** The subset of an `fb_records` (object 1) row this adapter reads. */
export interface FbAccountRow {
  id: string;
  fireberry_id: string | null;
  object_type_id: number;
  name_value: string | null;
  data: Record<string, any> | null;
  created_on: string | null;
  modified_on: string | null;
}

/** Flat, read-only account/business shape for the `fireberry_accounts` resource. */
export interface FireberryAccount {
  id: string;
  fireberry_id: string | null;
  name: string;
  phone: string | null;
  email: string | null;
  city: string | null;
  address: string | null;
  id_number: string | null;
  account_number: string | null;
  industry: string | null;
  primary_contact: string | null;
  primary_contact_id: string | null;
  parent_account: string | null;
  parent_account_id: string | null;
  status: string | null;
  description: string | null;
  created_at: string | null;
  updated_at: string | null;
  raw: Record<string, any> | null;
}

const firstNonEmpty = (...vals: (unknown | null | undefined)[]): string | null => {
  for (const v of vals) {
    if (typeof v === "string" && v.trim() !== "") return v;
    if (typeof v === "number") return String(v);
  }
  return null;
};

export const fbAccountToCompany = (row: FbAccountRow): FireberryAccount => {
  const d = row.data ?? {};

  const address =
    [d.billingstreet, d.billingzipcode, d.billingstate]
      .filter((p) => typeof p === "string" && p.trim() !== "")
      .join(", ")
      .trim() || null;

  return {
    id: row.id,
    fireberry_id: row.fireberry_id,
    name: firstNonEmpty(d.accountname, row.name_value ?? undefined) ?? "",
    phone: firstNonEmpty(d.telephone1, d.telephone2, d.telephone3),
    email: firstNonEmpty(d.emailaddress1, d.emailaddress2, d.emailaddress3),
    city: firstNonEmpty(d.billingcity),
    address,
    id_number: firstNonEmpty(d.idnumber),
    account_number: firstNonEmpty(d.accountnumber),
    industry: firstNonEmpty(d.industry, d.businesstypename),
    primary_contact: firstNonEmpty(d.primarycontactname),
    primary_contact_id: firstNonEmpty(d.primarycontactid),
    parent_account: firstNonEmpty(d.parentaccountname),
    parent_account_id: firstNonEmpty(d.parentaccountid),
    status: firstNonEmpty(d.status, d.statuscode),
    description: firstNonEmpty(d.description),
    created_at: row.created_on,
    updated_at: row.modified_on,
    raw: row.data,
  };
};

/** Fireberry object_type_id for "לקוח" / Account / בית עסק. */
export const FIREBERRY_ACCOUNT_OBJECT_TYPE = 1;
